import { Amplify } from "aws-amplify";
import { generateClient } from "aws-amplify/data";
import outputs from "../amplify_outputs.json";
import type { Schema } from "../../amplify/data/resource";

Amplify.configure(outputs);
const client = generateClient<Schema>();

class AmplifyService {
  constructor() { }

  async initialize() {
    return true;
  }

  async getGame(searchInput) {
    let nextToken: string | null | undefined = undefined;
    const games: any = [];

    try {
      while (true) {
        const options: any = {
          filter: {
            or: [
              {
                white: {
                  eq: searchInput,
                },
              },
              {
                black: {
                  eq: searchInput,
                },
              },
            ],
          },
          limit: 1000
        };

        if (nextToken) {
          options.nextToken = nextToken;
        }

        const response: any = await client.models.Game.list(options);
        if (response.data && response.data.length > 0) {
          games.push(...response.data);
        }

        nextToken = (response.nextToken && typeof response.nextToken === 'string' && response.nextToken.trim().length > 0) ? response.nextToken : null;

        if (!nextToken) {
          break;
        }
      }

      return games;
    } catch (error) {
      console.error("Error fetching games:", error);
      return [];
    }
  }

  async createUser(user) {
    try {
      const newUser = await client.models.User.create({
        ...user,
      });
      return newUser;
    } catch (error) {
      console.error("Error creating user:", error);
      return null;
    }
  }

  async createGame(game) {
    try {
      const newGame = await client.models.Game.create({
        ...game,
      });
      return newGame;
    } catch (error) {
      console.error("Error creating game:", error);
      return null;
    }
  }

  async deleteUser(userId) {
    if (!userId) {
      console.error("Invalid userId provided");
      return;
    }

    try {
      await client.models.User.delete({ id: userId });
    } catch (error) {
      console.error(`Failed to delete user with id ${userId}:`, error);
    }
  }

  async updateUser(user) {
    try {
      const updatedUser = await client.models.User.update(user);
      if (updatedUser.data) {
        return updatedUser;
      }
    } catch (error) {
      console.error("Error updating user:", error);
      return null;
    }
  }

  async getUser(userId) {
    try {
      const user = await client.models.User.get({ id: userId })
      return user.data;
    } catch (error) {
      console.error("Error getting user:", error);
      return null;
    }
  }

  async getRandomPuzzle(): Promise<{
    id: string;
    fen: string;
    whitePlayer: string;
    blackPlayer: string;
    date: string;
    moveIndex: number;
    turn: boolean;
    scores: { [key: string]: number };
  } | null> {
    try {
      // Tüm puzzle'ları çekmek için pagination kullan
      let allPuzzles: any[] = [];
      let nextToken: string | null | undefined = undefined;

      while (true) {
        const options: any = {
          limit: 1000 // Her seferinde 1000 tane çek
        };

        if (nextToken) {
          options.nextToken = nextToken;
        }

        const response = await client.models.Puzzle.list(options);
        const puzzles = response.data || [];
        const token = response.nextToken;

        if (puzzles && puzzles.length > 0) {
          allPuzzles = [...allPuzzles, ...puzzles];
        }

        nextToken = (token && typeof token === 'string' && token.trim().length > 0) ? token : null;

        if (!nextToken) {
          break;
        }
      }

      if (!allPuzzles || allPuzzles.length === 0) {
        return null;
      }

      const randomIndex = Math.floor(Math.random() * allPuzzles.length);
      const puzzle = allPuzzles[randomIndex];

      // Ensure all required fields exist
      if (!puzzle?.id || !(puzzle as any)?.fen || !puzzle?.whitePlayer || !puzzle?.blackPlayer ||
        !puzzle?.date || puzzle?.moveIndex === undefined || puzzle?.turn === undefined || !puzzle?.scores) {
        return null;
      }

      return {
        id: puzzle.id!,
        fen: (puzzle as any).fen as string,
        whitePlayer: puzzle.whitePlayer!,
        blackPlayer: puzzle.blackPlayer!,
        date: puzzle.date!,
        moveIndex: puzzle.moveIndex!,
        turn: puzzle.turn!,
        scores: puzzle.scores as { [key: string]: number }
      };
    } catch (error) {
      console.error("Puzzle çekilirken hata oluştu:", error);
      return null;
    }
  }

  async addSolvedPuzzle(userId: string, puzzleId: string) {
    try {
      // Önce kullanıcıyı getir
      const user = await client.models.User.get({ id: userId });

      if (!user.data) {
        console.error("User not found");
        return null;
      }

      // Mevcut çözülmüş puzzle'ları al veya boş dizi oluştur
      const solvedPuzzles = user.data.solvedPuzzles || [];

      // Eğer puzzle zaten çözülmüşse, işlem yapma
      if (solvedPuzzles.includes(puzzleId)) {
        return user.data;
      }

      // Yeni puzzle'ı listeye ekle
      const updatedSolvedPuzzles = [...solvedPuzzles, puzzleId];

      // Kullanıcıyı güncelle
      const updatedUser = await client.models.User.update({
        id: userId,
        solvedPuzzles: updatedSolvedPuzzles
      });

      return updatedUser.data;
    } catch (error) {
      console.error("Error adding solved puzzle:", error);
      return null;
    }
  }

  private sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private getPendingKey(userId: string): string {
    return `PendingSolves_${userId}`;
  }

  private readPendingSolves(userId: string): Array<{ puzzleId: string; move: string; elapsedTime: number }> {
    try {
      const raw = localStorage.getItem(this.getPendingKey(userId));
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (_e) {
      return [];
    }
  }

  private writePendingSolves(userId: string, items: Array<{ puzzleId: string; move: string; elapsedTime: number }>) {
    try {
      if (!items || items.length === 0) {
        localStorage.removeItem(this.getPendingKey(userId));
      } else {
        localStorage.setItem(this.getPendingKey(userId), JSON.stringify(items));
      }
    } catch (_e) {
      // ignore
    }
  }

  private enqueuePendingSolve(userId: string, item: { puzzleId: string; move: string; elapsedTime: number }) {
    const items = this.readPendingSolves(userId);
    if (!items.some((p) => p.puzzleId === item.puzzleId)) {
      items.push(item);
      this.writePendingSolves(userId, items);
    }
  }

  // Otoriter sunucu mutation'ını retry (exponential backoff) ile çağırır.
  // Parse edilmiş data döner ya da (kalıcı hata) null.
  private async callRecordMutation(args: { puzzleId: string; move: string; elapsedTime: number }): Promise<any | null> {
    const MAX_ATTEMPTS = 3;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const res: any = await (client as any).mutations.recordSolvedPuzzle({
          puzzleId: args.puzzleId,
          move: args.move,
          elapsedTime: args.elapsedTime,
        });
        if (res?.errors && res.errors.length > 0) {
          throw new Error(res.errors.map((e: any) => e?.message || e).join('; '));
        }
        const data = typeof res?.data === 'string' ? JSON.parse(res.data) : res?.data;
        if (data && data.success) {
          return data;
        }
        throw new Error((data && data.error) || 'recordSolvedPuzzle returned no success');
      } catch (error) {
        console.error(`recordSolvedPuzzle mutation attempt ${attempt}/${MAX_ATTEMPTS} failed:`, error);
        if (attempt < MAX_ATTEMPTS) {
          await this.sleep(300 * Math.pow(2, attempt - 1));
        }
      }
    }
    return null;
  }

  // Daha önce sunucuya gönderilememiş çözümleri yeniden göndermeyi dener.
  async flushPendingSolves(userId: string): Promise<void> {
    const items = this.readPendingSolves(userId);
    if (items.length === 0) return;
    const remaining: Array<{ puzzleId: string; move: string; elapsedTime: number }> = [];
    for (const item of items) {
      const data = await this.callRecordMutation(item);
      if (!data) remaining.push(item); // hâlâ başarısız → kuyrukta tut
    }
    this.writePendingSolves(userId, remaining);
  }

  /**
   * Bir puzzle çözümünü SUNUCU tarafında otoriter olarak skorlayıp kaydeder.
   * - Skor (delta) artık client'tan KABUL EDİLMEZ; Lambda puzzle.scores + move'dan hesaplar.
   * - solvedPuzzles + puzzleScores + puzzleTimes + puzzleSolvedAt tek update'te (atomik) yazılır.
   * - Ağ/yazım hatasında çözüm localStorage kuyruğuna alınır, bir sonraki çözümde tekrar denenir.
   *
   * İmza ve dönüş şekli eski (client-side) sürümle AYNI tutulmuştur; App.tsx'te tek
   * fark `move` parametresinin eklenmesidir. `delta`/`localScores` sadece sunucuya
   * ulaşılamayınca anlık (geçici) localStorage gösterimi için kullanılır; DB'ye asla
   * client kaynaklı skor yazılmaz.
   */
  async recordSolvedPuzzle(params: {
    userId: string;
    puzzleId: string;
    move?: string;
    delta: number;
    elapsedTime: number;
    solvedAtSeconds: number;
    localScores?: { [key: string]: number };
    localTimes?: { [key: string]: number };
    localSolvedAt?: { [key: string]: number };
  }): Promise<{
    success: boolean;
    puzzleScores: { [key: string]: number };
    puzzleTimes: { [key: string]: number };
    puzzleSolvedAt: { [key: string]: number };
    solvedPuzzles: string[];
    score: number;
    count: number;
    time: number;
  }> {
    const { userId, puzzleId, move, delta, elapsedTime, solvedAtSeconds } = params;
    const localScores = params.localScores || {};
    const localTimes = params.localTimes || {};
    const localSolvedAt = params.localSolvedAt || {};

    // 0) Önce bekleyen (daha önce gönderilememiş) çözümleri göndermeyi dene.
    try { await this.flushPendingSolves(userId); } catch (_e) { /* yut */ }

    // 1) Bu çözümü sunucuda otoriter skorla (retry'lı).
    const data = await this.callRecordMutation({ puzzleId, move: move || '', elapsedTime });
    if (data) {
      return {
        success: true,
        puzzleScores: data.puzzleScores || {},
        puzzleTimes: data.puzzleTimes || {},
        puzzleSolvedAt: data.puzzleSolvedAt || {},
        solvedPuzzles: Array.isArray(data.solvedPuzzles) ? data.solvedPuzzles : [],
        score: typeof data.score === 'number' ? data.score : 0,
        count: typeof data.count === 'number' ? data.count : 0,
        time: typeof data.time === 'number' ? data.time : 0,
      };
    }

    // 2) Başarısız → kuyruğa al ve OFFLINE fallback (yalnız local) döndür.
    //    Veri kaybolmaz; UI/localStorage tutarlı kalır, sonraki çözümde tekrar denenir.
    this.enqueuePendingSolve(userId, { puzzleId, move: move || '', elapsedTime });

    const fbScores = { ...localScores, [puzzleId]: delta };
    const fbTimes = { ...localTimes, [puzzleId]: elapsedTime };
    const fbSolvedAt = { ...localSolvedAt, [puzzleId]: solvedAtSeconds };
    const vals = Object.values(fbScores).filter(
      (s) => s !== undefined && s !== null && !Number.isNaN(s)
    ) as number[];
    const count = vals.length;
    const score = count > 0 ? Number((vals.reduce((a, b) => a + b, 0) / count).toFixed(3)) : 0;
    const time = Object.values(fbTimes).reduce(
      (acc: number, t) => acc + (typeof t === 'number' ? t : 0),
      0
    );
    return {
      success: false,
      puzzleScores: fbScores,
      puzzleTimes: fbTimes,
      puzzleSolvedAt: fbSolvedAt,
      solvedPuzzles: Array.from(new Set([...Object.keys(fbScores), puzzleId])),
      score,
      count,
      time,
    };
  }

  async getPuzzleBatch(count?: number): Promise<any[]> {
    try {
      // Her seferinde 1000 çekip nextToken ile pagination yapıyoruz
      let allPuzzles: any[] = [];
      let nextToken: string | null | undefined = undefined;
      let requestCount = 0;

      while (true) {
        requestCount++;
        // Her seferinde 1000 tane çek
        const limit = count ? Math.min(count, 1000) : 1000;

        const options: any = {
          limit: limit
        };

        // nextToken varsa mutlaka options'a ekle
        if (nextToken) {
          options.nextToken = nextToken;
        }

        const response = await client.models.Puzzle.list(options);
        const puzzles = response.data || [];
        const token = response.nextToken;

        if (puzzles && puzzles.length > 0) {
          allPuzzles = [...allPuzzles, ...puzzles];
        }

        // nextToken'ı bir sonraki istek için güncelle
        nextToken = (token && typeof token === 'string' && token.trim().length > 0) ? token : null;

        // Eğer count belirtilmişse, o kadar puzzle çekildiyse dur
        if (count && allPuzzles.length >= count) {
          allPuzzles = allPuzzles.slice(0, count);
          break;
        }

        // nextToken yoksa tüm puzzle'lar çekildi, dur
        if (!nextToken) {
          break;
        }
      }

      if (!allPuzzles || allPuzzles.length === 0) {
        return [];
      }

      const mappedPuzzles = allPuzzles.map(puzzle => {
        if (!puzzle?.id || !(puzzle as any)?.fen || !puzzle?.whitePlayer || !puzzle?.blackPlayer ||
          !puzzle?.date || puzzle?.moveIndex === undefined || puzzle?.turn === undefined || !puzzle?.scores) {
          return null;
        }

        return {
          id: puzzle.id!,
          fen: (puzzle as any).fen as string,
          whitePlayer: puzzle.whitePlayer!,
          blackPlayer: puzzle.blackPlayer!,
          date: puzzle.date!,
          moveIndex: puzzle.moveIndex!,
          turn: puzzle.turn!,
          scores: puzzle.scores as { [key: string]: number },
          createdAt: (puzzle as any).createdAt || null
        };
      }).filter(p => p !== null);

      // En eskiden en yeniye doğru sırala (createdAt'e göre - DB'ye kaydedilme tarihi)
      mappedPuzzles.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB; // Küçükten büyüğe (en eskiden en yeniye)
      });

      return mappedPuzzles;
    } catch (error) {
      console.error("Error fetching puzzle batch:", error);
      return [];
    }
  }

  async listUsersByScore(): Promise<Array<{ id: string; name?: string | null; score?: number | null; count?: number | null; time?: number | null; lastSolvedAt?: number | null }>> {
    try {
      // Tüm user'ları çekmek için pagination kullan
      let allUsers: any[] = [];
      let nextToken: string | null | undefined = undefined;

      while (true) {
        const options: any = {
          limit: 1000
        };

        if (nextToken) {
          options.nextToken = nextToken;
        }

        const response = await client.models.User.list(options);
        const users = response.data || [];

        if (users && users.length > 0) {
          allUsers = [...allUsers, ...users];
        }

        const token = response.nextToken;
        nextToken = (token && typeof token === 'string' && token.trim().length > 0) ? token : null;

        if (!nextToken) {
          break;
        }
      }

      const normalized = (allUsers || []).map((u: any) => {
        // puzzleScores'dan sadece puanı olan puzzle'ları hesaba katarak average score hesapla
        let calculatedScore = 0;
        let scoredPuzzleCount = 0;

        let calculatedTime = 0;

        try {
          let puzzleScores: { [key: string]: number } = {};
          if (u.puzzleScores) {
            if (typeof u.puzzleScores === 'string') {
              const parsed = JSON.parse(u.puzzleScores);
              if (parsed && typeof parsed === 'object') {
                puzzleScores = parsed as { [key: string]: number };
              }
            } else if (typeof u.puzzleScores === 'object') {
              puzzleScores = u.puzzleScores as { [key: string]: number };
            }
          }

          // Sadece puanı olan puzzle'ları filtrele ve average hesapla
          const scores = Object.values(puzzleScores).filter(
            (score) => score !== undefined && score !== null && !Number.isNaN(score)
          );

          if (scores.length > 0) {
            const sum = scores.reduce((acc, score) => acc + score, 0);
            calculatedScore = sum / scores.length;
            scoredPuzzleCount = scores.length;
          }

          // puzzleTimes'ı al ve sadece puanı olan puzzle'ların time'ını topla
          let puzzleTimes: { [key: string]: number } = {};
          if ((u as any).puzzleTimes) {
            try {
              if (typeof (u as any).puzzleTimes === 'string') {
                const parsedTimes = JSON.parse((u as any).puzzleTimes);
                if (parsedTimes && typeof parsedTimes === 'object') {
                  puzzleTimes = parsedTimes;
                }
              } else if (typeof (u as any).puzzleTimes === 'object') {
                puzzleTimes = (u as any).puzzleTimes as { [key: string]: number };
              }
            } catch (_e) {
              puzzleTimes = {};
            }
          }

          // Sadece puanı olan puzzle'ların time'ını topla (puzzleScores ile aynı puzzle ID'leri)
          calculatedTime = Object.keys(puzzleScores)
            .filter(puzzleId => puzzleScores[puzzleId] !== undefined && puzzleScores[puzzleId] !== null && !Number.isNaN(puzzleScores[puzzleId]))
            .reduce((acc, puzzleId) => acc + (puzzleTimes[puzzleId] || 0), 0);
        } catch (err) {
          // Hata durumunda eski score'u kullan
          calculatedScore = (u as any).score ?? 0;
          scoredPuzzleCount = (u as any).count ?? 0;
          calculatedTime = (u as any).time ?? 0;
        }

        // puzzleSolvedAt'tan en son çözülen puzzle'ın zamanını bul
        let lastSolvedAt: number | null = null;
        try {
          let puzzleSolvedAt: { [key: string]: number } = {};
          if ((u as any).puzzleSolvedAt) {
            if (typeof (u as any).puzzleSolvedAt === 'string') {
              const parsedSolvedAt = JSON.parse((u as any).puzzleSolvedAt);
              if (parsedSolvedAt && typeof parsedSolvedAt === 'object') {
                puzzleSolvedAt = parsedSolvedAt;
              }
            } else if (typeof (u as any).puzzleSolvedAt === 'object') {
              puzzleSolvedAt = (u as any).puzzleSolvedAt as { [key: string]: number };
            }
          }

          // En son çözülen puzzle'ın timestamp'ini bul (en büyük değer)
          const timestamps = Object.values(puzzleSolvedAt).filter(t => typeof t === 'number' && !Number.isNaN(t));
          if (timestamps.length > 0) {
            lastSolvedAt = Math.max(...timestamps);
          }
        } catch (err) {
          lastSolvedAt = null;
        }

        return {
          id: u.id as string,
          name: (u as any).name ?? null,
          score: calculatedScore,
          count: scoredPuzzleCount,
          time: calculatedTime,
          lastSolvedAt,
        };
      });
      normalized.sort((a, b) => {
        const now = Math.floor(Date.now() / 1000); // Unix timestamp (seconds)
        const fiveDaysInSeconds = 5 * 24 * 60 * 60; // 5 gün = 432000 saniye

        // Inactive kontrolü: 5 günden fazla soru çözmemiş veya hiç soru çözmemiş
        const aInactive = !a.lastSolvedAt || (now - a.lastSolvedAt) > fiveDaysInSeconds || (a.count || 0) === 0;
        const bInactive = !b.lastSolvedAt || (now - b.lastSolvedAt) > fiveDaysInSeconds || (b.count || 0) === 0;

        // Inactive user'ları en sona gönder
        if (aInactive && !bInactive) return 1;
        if (!aInactive && bInactive) return -1;

        // İkisi de inactive ise veya ikisi de active ise, score'a göre sırala
        // Küçük score önce (daha iyi ortalama)
        return (a.score || 0) - (b.score || 0);
      });
      return normalized.slice(0, 100);
    } catch (error) {
      console.error('Error listing users by score:', error);
      return [];
    }
  }

  async listUsersByLatestPuzzles(): Promise<Array<{ id: string; name?: string | null; score?: number | null; count?: number | null; time?: number | null; lastSolvedAt?: number | null }>> {
    try {
      // Önce tüm puzzle'ları çek ve en son batch'i bul
      const allPuzzles = await this.getPuzzleBatch();

      if (!allPuzzles || allPuzzles.length === 0) {
        return [];
      }

      // createdAt'e göre sırala ve en son batch'i bul
      // En son eklenen puzzle'ın tarihini bul
      const sortedByDate = [...allPuzzles].sort((a, b) => {
        const dateA = (a as any).createdAt ? new Date((a as any).createdAt).getTime() : 0;
        const dateB = (b as any).createdAt ? new Date((b as any).createdAt).getTime() : 0;
        return dateB - dateA; // En yeniden en eskiye
      });

      const latestPuzzle = sortedByDate[0];
      const latestDate = (latestPuzzle as any).createdAt;

      if (!latestDate) {
        // Eğer createdAt yoksa, normal sıralamayı döndür
        return this.listUsersByScore();
      }

      // En son puzzle'ın eklendiği GÜN'ü bul (00:00:00 - 23:59:59)
      const latestDateObj = new Date(latestDate);

      // O günün başlangıcı (00:00:00)
      const dayStart = new Date(latestDateObj);
      dayStart.setHours(0, 0, 0, 0);

      // O günün bitişi (23:59:59)
      const dayEnd = new Date(latestDateObj);
      dayEnd.setHours(23, 59, 59, 999);

      const dayStartTime = dayStart.getTime();
      const dayEndTime = dayEnd.getTime();

      // O gün içinde eklenen tüm puzzle'ları al
      const latestPuzzles = allPuzzles.filter(p => {
        const createdAt = (p as any).createdAt;
        if (!createdAt) return false;
        const puzzleTime = new Date(createdAt).getTime();
        return puzzleTime >= dayStartTime && puzzleTime <= dayEndTime;
      });

      console.log(`[Latest Puzzles] Total puzzles: ${allPuzzles.length}`);
      console.log(`[Latest Puzzles] Latest batch size: ${latestPuzzles.length}`);
      console.log(`[Latest Puzzles] Latest date: ${latestDate}`);
      console.log(`[Latest Puzzles] Day range: ${dayStart.toISOString()} - ${dayEnd.toISOString()}`);

      const latestPuzzleIds = new Set(latestPuzzles.map(p => p.id));

      // Tüm user'ları çek
      let allUsers: any[] = [];
      let nextToken: string | null | undefined = undefined;

      while (true) {
        const options: any = { limit: 1000 };
        if (nextToken) {
          options.nextToken = nextToken;
        }

        const response = await client.models.User.list(options);
        const users = response.data || [];

        if (users && users.length > 0) {
          allUsers = [...allUsers, ...users];
        }

        const token = response.nextToken;
        nextToken = (token && typeof token === 'string' && token.trim().length > 0) ? token : null;

        if (!nextToken) {
          break;
        }
      }

      // Her user için sadece son puzzle'lardaki skorları hesapla
      const normalized = (allUsers || []).map((u: any) => {
        let calculatedScore = 0;
        let scoredPuzzleCount = 0;
        let calculatedTime = 0;

        try {
          let puzzleScores: { [key: string]: number } = {};
          if (u.puzzleScores) {
            if (typeof u.puzzleScores === 'string') {
              const parsed = JSON.parse(u.puzzleScores);
              if (parsed && typeof parsed === 'object') {
                puzzleScores = parsed as { [key: string]: number };
              }
            } else if (typeof u.puzzleScores === 'object') {
              puzzleScores = u.puzzleScores as { [key: string]: number };
            }
          }

          // Sadece son batch'teki puzzle'ların skorlarını filtrele
          const latestScores = Object.entries(puzzleScores)
            .filter(([puzzleId]) => latestPuzzleIds.has(puzzleId))
            .map(([, score]) => score)
            .filter((score) => score !== undefined && score !== null && !Number.isNaN(score));

          if (latestScores.length > 0) {
            const sum = latestScores.reduce((acc, score) => acc + score, 0);
            calculatedScore = sum / latestScores.length;
            scoredPuzzleCount = latestScores.length;
          }

          // puzzleTimes'ı al
          let puzzleTimes: { [key: string]: number } = {};
          if ((u as any).puzzleTimes) {
            try {
              if (typeof (u as any).puzzleTimes === 'string') {
                const parsedTimes = JSON.parse((u as any).puzzleTimes);
                if (parsedTimes && typeof parsedTimes === 'object') {
                  puzzleTimes = parsedTimes;
                }
              } else if (typeof (u as any).puzzleTimes === 'object') {
                puzzleTimes = (u as any).puzzleTimes as { [key: string]: number };
              }
            } catch (_e) {
              puzzleTimes = {};
            }
          }

          // Sadece son batch'teki puzzle'ların time'ını topla
          calculatedTime = Object.keys(puzzleScores)
            .filter(puzzleId => latestPuzzleIds.has(puzzleId) && puzzleScores[puzzleId] !== undefined)
            .reduce((acc, puzzleId) => acc + (puzzleTimes[puzzleId] || 0), 0);
        } catch (err) {
          calculatedScore = 0;
          scoredPuzzleCount = 0;
          calculatedTime = 0;
        }

        // lastSolvedAt hesapla (tüm puzzle'lardan)
        let lastSolvedAt: number | null = null;
        try {
          let puzzleSolvedAt: { [key: string]: number } = {};
          if ((u as any).puzzleSolvedAt) {
            if (typeof (u as any).puzzleSolvedAt === 'string') {
              const parsedSolvedAt = JSON.parse((u as any).puzzleSolvedAt);
              if (parsedSolvedAt && typeof parsedSolvedAt === 'object') {
                puzzleSolvedAt = parsedSolvedAt;
              }
            } else if (typeof (u as any).puzzleSolvedAt === 'object') {
              puzzleSolvedAt = (u as any).puzzleSolvedAt as { [key: string]: number };
            }
          }

          const timestamps = Object.values(puzzleSolvedAt).filter(t => typeof t === 'number' && !Number.isNaN(t));
          if (timestamps.length > 0) {
            lastSolvedAt = Math.max(...timestamps);
          }
        } catch (err) {
          lastSolvedAt = null;
        }

        return {
          id: u.id as string,
          name: (u as any).name ?? null,
          score: calculatedScore,
          count: scoredPuzzleCount,
          time: calculatedTime,
          lastSolvedAt,
        };
      });

      // Sıralama - inactive user'ları sona gönder
      normalized.sort((a, b) => {
        const now = Math.floor(Date.now() / 1000);
        const fiveDaysInSeconds = 5 * 24 * 60 * 60;

        const aInactive = !a.lastSolvedAt || (now - a.lastSolvedAt) > fiveDaysInSeconds || (a.count || 0) === 0;
        const bInactive = !b.lastSolvedAt || (now - b.lastSolvedAt) > fiveDaysInSeconds || (b.count || 0) === 0;

        if (aInactive && !bInactive) return 1;
        if (!aInactive && bInactive) return -1;

        return (a.score || 0) - (b.score || 0);
      });

      return normalized.slice(0, 100);
    } catch (error) {
      console.error('Error listing users by latest puzzles:', error);
      return [];
    }
  }

  async getTotalPuzzleCount(): Promise<number> {
    try {
      // Tüm puzzle'ları çekmek için pagination kullan
      let totalCount = 0;
      let nextToken: string | null | undefined = undefined;

      while (true) {
        const options: any = {
          limit: 1000 // Her seferinde 1000 tane çek
        };

        if (nextToken) {
          options.nextToken = nextToken;
        }

        const response = await client.models.Puzzle.list(options);
        const puzzles = response.data || [];
        const token = response.nextToken;

        totalCount += puzzles.length;

        nextToken = (token && typeof token === 'string' && token.trim().length > 0) ? token : null;

        if (!nextToken) {
          break;
        }
      }

      return totalCount;
    } catch (error) {
      console.error('Error getting total puzzle count:', error);
      return 0;
    }
  }

  async getPuzzlesByIds(puzzleIds: string[]): Promise<Array<{
    id: string;
    fen: string;
    whitePlayer: string;
    blackPlayer: string;
    date: string;
    moveIndex: number;
    turn: boolean;
    scores: { [key: string]: number };
  }>> {
    try {
      if (!puzzleIds || puzzleIds.length === 0) {
        return [];
      }

      // Tüm puzzle'ları getir ve ID'lere göre filtrele
      const puzzles = await Promise.all(
        puzzleIds.map(async (id) => {
          try {
            const response = await client.models.Puzzle.get({ id });
            if (response.data && response.data.id) {
              return {
                id: response.data.id,
                fen: (response.data as any).fen as string,
                whitePlayer: response.data.whitePlayer!,
                blackPlayer: response.data.blackPlayer!,
                date: response.data.date!,
                moveIndex: response.data.moveIndex!,
                turn: response.data.turn!,
                scores: response.data.scores as { [key: string]: number }
              };
            }
            console.warn(`⚠️ Puzzle ${id} data is null or incomplete`);
            return null;
          } catch (error) {
            console.error(`❌ Puzzle ${id} not found in DB:`, error);
            return null;
          }
        })
      );

      const validPuzzles = puzzles.filter((p): p is {
        id: string;
        fen: string;
        whitePlayer: string;
        blackPlayer: string;
        date: string;
        moveIndex: number;
        turn: boolean;
        scores: { [key: string]: number };
      } => p !== null);

      const missingCount = puzzleIds.length - validPuzzles.length;
      if (missingCount > 0) {
        console.warn(`⚠️ ${missingCount} out of ${puzzleIds.length} solved puzzles are missing from database!`);
        console.warn(`   Showing ${validPuzzles.length} puzzles.`);
      }

      return validPuzzles;
    } catch (error) {
      console.error('Error getting puzzles by IDs:', error);
      return [];
    }
  }

  async listFeedback(): Promise<Array<{
    id: string;
    content: string;
    createdBy?: string | null;
    createdAt?: string | null;
    done?: boolean | null;
  }>> {
    try {
      // Tüm feedback'leri çekmek için pagination kullan
      let allItems: any[] = [];
      let nextToken: string | null | undefined = undefined;

      while (true) {
        const options: any = {
          limit: 1000
        };

        if (nextToken) {
          options.nextToken = nextToken;
        }

        const response = await client.models.Feedback.list(options);
        const items = response.data || [];

        if (items && items.length > 0) {
          allItems = [...allItems, ...items];
        }

        const token = response.nextToken;
        nextToken = (token && typeof token === 'string' && token.trim().length > 0) ? token : null;

        if (!nextToken) {
          break;
        }
      }

        return allItems
        .map((item) => ({
          id: item.id!,
          content: item.content!,
          createdBy: item.createdBy ?? null,
          createdAt: item.createdAt ?? null,
          done: item.done ?? false,
          upvotes: item.upvotes ?? 0,
          downvotes: item.downvotes ?? 0,
        }))
        .sort((a, b) => {
          const aDone = a.done ?? false;
          const bDone = b.done ?? false;
          if (aDone && !bDone) return 1;
          if (!aDone && bDone) return -1;
          const aScore = (a.upvotes ?? 0) - (a.downvotes ?? 0);
          const bScore = (b.upvotes ?? 0) - (b.downvotes ?? 0);
          if (bScore !== aScore) return bScore - aScore;
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bTime - aTime;
        });
    } catch (error) {
      console.error('Error listing feedback:', error);
      return [];
    }
  }

  async addFeedback(content: string, createdBy: string): Promise<{
    id: string;
    content: string;
    createdBy?: string | null;
    createdAt?: string | null;
    done?: boolean | null;
  } | null> {
    try {
      const now = new Date().toISOString();
      const result = await client.models.Feedback.create({
        content,
        createdBy,
        createdAt: now,
        upvotes: 0,
        downvotes: 0,
      });
      if (result?.data) {
        return {
          id: result.data.id!,
          content: result.data.content!,
          createdBy: result.data.createdBy ?? null,
          createdAt: result.data.createdAt ?? now,
          done: result.data.done ?? false,
          upvotes: 0,
          downvotes: 0,
        };
      }
      return null;
    } catch (error) {
      console.error('Error adding feedback:', error);
      return null;
    }
  }

  async updateFeedbackStatus(id: string, done: boolean) {
    try {
      const updated = await client.models.Feedback.update({
        id,
        done
      });
      return updated.data;
    } catch (error) {
      console.error('Error updating feedback status:', error);
      return null;
    }
  }

  async listMyFeedbackVotes(userId: string): Promise<Record<string, number>> {
    try {
      const map: Record<string, number> = {};
      let nextToken: string | null | undefined = undefined;
      while (true) {
        const options: any = { limit: 500, filter: { userId: { eq: userId } } };
        if (nextToken) options.nextToken = nextToken;
        const res = await client.models.FeedbackVote.list(options);
        for (const v of res.data || []) {
          if (v.feedbackId && v.value != null) map[v.feedbackId] = v.value;
        }
        nextToken = res.nextToken;
        if (!nextToken) break;
      }
      return map;
    } catch (error) {
      console.error('Error listing feedback votes:', error);
      return {};
    }
  }

  async voteFeedback(feedbackId: string, userId: string, value: 1 | -1): Promise<{ upvotes: number; downvotes: number } | null> {
    try {
      const res = await client.models.FeedbackVote.list({
        filter: { feedbackId: { eq: feedbackId }, userId: { eq: userId } },
        limit: 1,
      });
      const existing = res.data?.[0];
      const feedback = await client.models.Feedback.get({ id: feedbackId });
      const currentUp = feedback.data?.upvotes ?? 0;
      const currentDown = feedback.data?.downvotes ?? 0;

      if (existing) {
        const oldVal = (existing.value ?? 0) as number;
        if (oldVal === value) return { upvotes: currentUp, downvotes: currentDown };
        await client.models.FeedbackVote.update({ id: existing.id, value });
        const deltaUp = (value === 1 ? 1 : 0) - (oldVal === 1 ? 1 : 0);
        const deltaDown = (value === -1 ? 1 : 0) - (oldVal === -1 ? 1 : 0);
        const newUp = Math.max(0, currentUp + deltaUp);
        const newDown = Math.max(0, currentDown + deltaDown);
        await client.models.Feedback.update({ id: feedbackId, upvotes: newUp, downvotes: newDown });
        return { upvotes: newUp, downvotes: newDown };
      } else {
        await client.models.FeedbackVote.create({ feedbackId, userId, value });
        const newUp = currentUp + (value === 1 ? 1 : 0);
        const newDown = currentDown + (value === -1 ? 1 : 0);
        await client.models.Feedback.update({ id: feedbackId, upvotes: newUp, downvotes: newDown });
        return { upvotes: newUp, downvotes: newDown };
      }
    } catch (error) {
      console.error('Error voting feedback:', error);
      return null;
    }
  }

  async getPuzzle(puzzleId: string) {
    try {
      const puzzle = await client.models.Puzzle.get({ id: puzzleId });
      return puzzle.data;
    } catch (error) {
      console.error("Error fetching puzzle:", error);
      return null;
    }
  }

  async updatePuzzleMoveStats(puzzleId: string, moveSan: string) {
    try {
      const puzzle = await client.models.Puzzle.get({ id: puzzleId });
      if (!puzzle.data) return;

      let moveStats: { [key: string]: number } = {};

      if (puzzle.data.moveStats) {
        if (typeof puzzle.data.moveStats === 'string') {
          moveStats = JSON.parse(puzzle.data.moveStats);
        } else {
          moveStats = puzzle.data.moveStats as { [key: string]: number };
        }
      }

      // Update count for the move
      moveStats[moveSan] = (moveStats[moveSan] || 0) + 1;

      await client.models.Puzzle.update({
        id: puzzleId,
        moveStats: JSON.stringify(moveStats)
      });
    } catch (error) {
      console.error("Error updating puzzle move stats:", error);
    }
  }
}

// Export an instance of the AmplifyService class
export default new AmplifyService();
